import moment from 'moment';
import { getCurrentLanguage } from 'modules/language/selectors/language';

moment.calendarFormat = (myMoment, now) => {
  const diff = myMoment.diff(now, 'days', true);

  return diff < -6 ? 'sameElse' :
    diff < -1 ? 'lastWeek' :
      diff < 0 ? 'lastDay' :
        diff < 1 ? 'sameDay' :
          diff < 2 ? 'nextDay' :
            (myMoment.isoWeek() === now.isoWeek() && myMoment.year() === now.year()) ? 'thisWeek' :
              diff < 7 ? 'nextWeek' : 'sameElse';
};

const getMomentDate = (timeStamp) => {
  return moment.unix(timeStamp / 1000);
};

export const getDurationFormatted = (startTime, endTime) => {
  const inputStartTime = getMomentDate(startTime);
  const inputEndTime = getMomentDate(endTime);

  const diff = Math.abs(inputEndTime.diff(inputStartTime, 'seconds'));

  return moment().startOf('day')
    .seconds(diff)
    .format('HH:mm:ss');
};

export const getCloseTimeFormatted = (timeStamp, intl) => {
  const inputDate = getMomentDate(timeStamp);
  const defaultDateFormat = `MMM DD, YYYY [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`;

  return inputDate
    .locale(intl.locale)
    .calendar(
      null,
      {
        sameDay: `[${ intl.formatMessage({ id: 'General.Today', defaultMessage: '** Today' }) } ${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`,
        lastDay: `[${ intl.formatMessage({ id: 'General.Yesterday', defaultMessage: '** Yesterday' }) } ${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`,
        nextDay: defaultDateFormat,
        lastWeek: defaultDateFormat,
        thisWeek: defaultDateFormat,
        nextWeek: defaultDateFormat,
        sameElse: defaultDateFormat,
      },
    );
};

export const getScheduleTimeFormatted = (startTime, endTime, intl) => {
  const inputStartTime = getMomentDate(startTime);
  const inputEndTime = getMomentDate(endTime);

  let hoursTimeRange = `${ inputStartTime.format('HH:mm') }`;
  if (endTime !== 'null') {
    hoursTimeRange = `${ hoursTimeRange } - ${ inputEndTime.format('HH:mm') }`;
  }

  const defaultDateFormat = `MMM DD, YYYY [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) } ${ hoursTimeRange }]`;

  return inputStartTime
    .locale(intl.locale)
    .calendar(
      null,
      {
        sameDay: `[${ intl.formatMessage({ id: 'General.Today', defaultMessage: '** Today' }) } ${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) } ${ hoursTimeRange }]`,
        nextDay: `[${ intl.formatMessage({ id: 'General.Tomorrow', defaultMessage: '** Tomorrow' }) } ${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) } ${ hoursTimeRange }]`,
        lastDay: `[${ intl.formatMessage({ id: 'General.Yesterday', defaultMessage: '** Yesterday' }) } ${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) } ${ hoursTimeRange }]`,
        thisWeek: defaultDateFormat,
        lastWeek: defaultDateFormat,
        nextWeek: defaultDateFormat,
        sameElse: defaultDateFormat,
      },
    );
};

export const getLastConversationDateFormatted = (timeStamp, intl) => {
  const inputDate = getMomentDate(timeStamp);
  const defaultDateFormat = `MMM DD, YYYY [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`;

  return inputDate
    .locale(intl.locale)
    .calendar(
      null,
      {
        sameDay: 'HH:mm',
        lastDay: `[${ intl.formatMessage({ id: 'General.Yesterday', defaultMessage: '** Yesterday' }) }] [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`,
        nextDay: defaultDateFormat,
        thisWeek: defaultDateFormat,
        lastWeek: defaultDateFormat,
        nextWeek: defaultDateFormat,
        sameElse: defaultDateFormat,
      },
    );
};

export const getDateFormatted = (timeStamp, format) => {
  const inputDate = getMomentDate(timeStamp);

  return inputDate
    .locale(getCurrentLanguage())
    .calendar(
      null,
      {
        sameDay: format,
        lastDay: format,
        nextDay: format,
        thisWeek: format,
        lastWeek: format,
        nextWeek: format,
        sameElse: format,
      },
    );
};


export const getAppointmentStartTimeFormatted = (startTime, endTime, intl) => {
  const inputStartTime = getMomentDate(startTime);
  const inputEndTime = getMomentDate(endTime);

  const minutesDiff = inputStartTime.diff(moment(), 'minutes');

  if (minutesDiff < 3) {
    return intl.formatMessage({ id: 'Appointment.View.ConversationPanel.AppointmentStartsNow.Label', defaultMessage: '** Appointment starts now' });
  }

  if (minutesDiff < 60) {
    return intl.formatMessage(
      { id: 'Appointment.View.ConversationPanle.MinutesToStart', defaultMessage: `** Appointment starts in ${ minutesDiff } minutes` },
      { minutesToStart: minutesDiff },
    );
  }

  let hoursTimeRange = inputStartTime.format('HH:mm');
  if (endTime) hoursTimeRange += ` - ${ inputEndTime.format('HH:mm') }`;

  const defaultDateFormat = `MMM DD, YYYY [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) } ${ hoursTimeRange }]`;

  return intl.formatMessage({ id: 'Appointment.View.ConversationPanle.AppointmentScheduledFor.Label', defaultMessage: '** Appointment is scheduled for' }) + '\n' + inputStartTime
    .locale(intl.locale)
    .calendar(
      null,
      {
        sameDay: `[${ intl.formatMessage({ id: 'General.Today', defaultMessage: '** Today' }) }]`,
        nextDay: `[${ intl.formatMessage({ id: 'General.Tomorrow', defaultMessage: '** Tomorrow' }) }]`,
        thisWeek: 'dddd',
        lastDay: defaultDateFormat,
        lastWeek: defaultDateFormat,
        nextWeek: defaultDateFormat,
        sameElse: defaultDateFormat,
      },
    );
};

export const isSameDay = (startTime) => {
  return moment.unix(startTime / 1000)
    .endOf('day')
    .diff(moment(), 'days') === 0;
};

export const isOverdue = (startTime) => {
  return moment.unix(startTime / 1000).diff(moment(), 'minutes') < 0;
};

export const getUpcomingDiffTime = (startTime) => {
  const currTime = moment();
  if (currTime > startTime) return 1;

  return moment.unix(startTime / 1000).diff(currTime, 'minutes');
};

export const getAddedToQueueDateFormatted = (time, intl) =>
  moment(+time).locale(intl.locale).format(`MMM DD, YYYY [${ intl.formatMessage({ id: 'const.at', defaultMessage: '** at' }) }] HH:mm`);

